import analytics from '../analytics'

class ErrorTrackingService {
  constructor() {
    this.errorCounts = new Map()
    this.sessionId = this.generateSessionId()
    this.maxErrorsPerSession = 5
    this.userInfo = null
  }

  setUserInfo(userInfo) {
    this.userInfo = {
      userId: userInfo.userId,
      email: userInfo.email,
      name: userInfo.name,
      objective: userInfo.objective,
      mode: userInfo.mode
    }
  }

  getUserId() {
    return this.userInfo?.userId || 'anonymous'
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
  }

  shouldTrackError(errorKey) {
    const count = this.errorCounts.get(errorKey) || 0
    if (count >= this.maxErrorsPerSession) {
      console.warn(`Error tracking limit reached for: ${errorKey}`)
      return false
    }
    this.errorCounts.set(errorKey, count + 1)
    return true
  }

  trackError(errorType, errorData, eventName = 'Error Occurred') {
    const errorKey = `${errorType}_${errorData.message || 'unknown'}`

    if (!this.shouldTrackError(errorKey)) {
      return
    }

    const trackingData = {
      ...errorData,
      error_type: errorType,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      // User info
      user_id: this.userInfo?.userId || 'anonymous',
      user_email: this.userInfo?.email || 'unknown',
      user_name: this.userInfo?.name || 'unknown',
      user_objective: this.userInfo?.objective || 'unknown',
      user_mode: this.userInfo?.mode || 'unknown'
    }

    try {
      analytics.track(eventName, trackingData)
      console.log('Error tracked to Amplitude:', eventName, trackingData)
    } catch (trackingError) {
      console.error('Failed to track error to Amplitude:', trackingError)
    }
  }

  trackAPIError(error, endpoint, method = 'GET', eventName = 'Error Occurred', additionalInfo = {}) {
    const errorData = {
      message: error.message || 'Unknown API error',
      endpoint,
      method,
      status_code: error.response?.status || 'unknown',
      url: window.location.href,
      pathname: window.location.pathname,
      ...additionalInfo
    }

    if (error.response?.data) {
      errorData.response_data = JSON.stringify(error.response.data).substring(0, 500)
    }

    if (error.stack) {
      errorData.stack_trace = error.stack.substring(0, 1000)
    }

    this.trackError('API_Error', errorData, eventName)
  }

  trackJavaScriptError(error, additionalInfo = {}) {
    const errorData = {
      message: error.message || 'Unknown JavaScript error',
      name: error.name || 'Error',
      url: window.location.href,
      pathname: window.location.pathname,
      ...additionalInfo
    }

    if (error.stack) {
      errorData.stack_trace = error.stack.substring(0, 1000)
    }

    if (additionalInfo.componentStack) {
      errorData.component_stack = additionalInfo.componentStack.substring(0, 1000)
    }

    this.trackError('JavaScript_Error', errorData)
  }
}

const errorTrackingService = new ErrorTrackingService()

export default errorTrackingService
